import { useMemo } from "react";
import { REQUEST_ID } from "@sablier/v2-constants";
import { _ } from "@sablier/v2-mixins";
import { Token } from "@sablier/v2-models";
import { useQuery } from "@tanstack/react-query";
import type { IToken, UniswapTokenList } from "@sablier/v2-types";

interface Props {
  isTemporarilyCached?: boolean;
  url: string | undefined;
}

export async function requestTokenList({
  url,
}: Props): Promise<UniswapTokenList> {
  try {
    if (_.isNilOrEmptyString(url)) {
      throw new Error("Missing list url.");
    }
    const response = await fetch(url, {
      credentials: "omit",
      method: "GET",
    });

    if (response.ok) {
      return response.json();
    } else {
      const error = await _.attemptAsync(async () => response.json());
      if (!_.isError(error) && _.has(error, "message")) {
        throw new Error(error.message);
      }
      throw new Error(`Connection error ${response.status}`);
    }
  } catch (error) {
    throw new Error(`Token list failed to be fetched and/or validated: ${url}`);
  }
}

export default function useRequestTokenList({ url }: Props) {
  const {
    data: raw,
    error,
    isLoading,
  } = useQuery<UniswapTokenList | undefined>({
    queryKey: [...REQUEST_ID.tokenList, { unique: url }],
    queryFn: async () => requestTokenList({ url }),
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    enabled: !_.isNilOrEmptyString(url),
  });

  const list: IToken[] = useMemo(() => {
    if (!raw) {
      return [];
    }
    return raw.tokens
      .map((item) => {
        const image = _.toString(_.get(item, "logoURI"));

        const token = Token.fromUniswapToken(item);
        token.image = image.startsWith("https")
          ? image
          : image.startsWith("ipfs://")
          ? image.replace("ipfs://", "https://cloudflare-ipfs.com/ipfs/")
          : undefined;
        return token;
      })
      .sort((a, b) => a.symbol.localeCompare(b.symbol))
      .map((item) => new Token(item));
  }, [raw]);

  return {
    error,
    isLoading,
    list,
    raw,
    request: requestTokenList,
  };
}
